import React from 'react';
import {
  Plus,
  Search,
  Filter as FilterIcon,
  ChevronDown,
  CheckSquare,
  Play,
  CheckCircle2,
  User,
  Users,
  Archive,
  X as XIcon,
  ExternalLink,
  Eye,
  Edit,
  Trash2,
  ClipboardList,
} from 'lucide-react';
import Avatar from './Avatar';
import {
  StatCard,
  StatusPill,
  TablePagination,
  RowActionsMenu,
} from './ui';
import { formatDurationHM, projectDotColor } from './ui/formatters';

const COMPLETED_STATUSES = new Set(['Done', 'In QA', 'Completed']);
const ACTIVE_STATUSES = new Set(['In Progress', 'To Do']);

const PRIORITY_OPTIONS = [
  { id: 'urgent', name: 'Urgent' },
  { id: 'high', name: 'High' },
  { id: 'medium', name: 'Medium' },
  { id: 'low', name: 'Low' },
];

const OVERDUE_OPTIONS = [
  { id: 'true', name: 'Overdue Only' },
  { id: 'false', name: 'Not Overdue' },
];

const GITHUB_OPTIONS = [
  { id: 'connected', name: 'GitHub Connected' },
  { id: 'not_connected', name: 'GitHub Not Connected' },
];

const TasksListView = ({
  user,
  title,
  subtitle,
  taskScope = 'all',
  onTaskScopeChange,
  searchTerm,
  onSearchChange,
  showFilters,
  onToggleFilters,
  filters,
  onFilterChange,
  onClearFilters,
  taskStatuses,
  availableMembers,
  projects,
  filteredTasks,
  paginatedTasks,
  serverTotal,
  isFetching = false,
  statistics,
  currentPage,
  pageSize,
  pageSizeOptions,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onCreate,
  onView,
  onOpenInNewWindow,
  onEdit,
  onDelete,
  showDeletedTasks,
  onToggleDeleted,
  deletedTasksCount,
  deletedTasksLoading,
  deletedTasks,
  onRestore,
  canManageTask,
  getProjectName,
  getStatusColor,
  filterBanner,
}) => {
  const [openFilterDropdown, setOpenFilterDropdown] = React.useState(null);
  const [statusSearch, setStatusSearch] = React.useState('');
  const [prioritySearch, setPrioritySearch] = React.useState('');
  const [assigneeSearch, setAssigneeSearch] = React.useState('');
  const [projectSearch, setProjectSearch] = React.useState('');
  const [overdueSearch, setOverdueSearch] = React.useState('');
  const [githubSearch, setGithubSearch] = React.useState('');

  const membersById = React.useMemo(() => {
    const map = new Map();
    (availableMembers || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [availableMembers]);

  const statusOptions = React.useMemo(
    () => (taskStatuses || []).map((s) => ({ id: s.name, name: s.name })),
    [taskStatuses]
  );

  const filteredStatuses = React.useMemo(() => {
    if (!statusSearch.trim()) return statusOptions;
    const term = statusSearch.toLowerCase().trim();
    return statusOptions.filter((s) => s.name.toLowerCase().includes(term));
  }, [statusOptions, statusSearch]);

  const filteredPriorities = React.useMemo(() => {
    if (!prioritySearch.trim()) return PRIORITY_OPTIONS;
    const term = prioritySearch.toLowerCase().trim();
    return PRIORITY_OPTIONS.filter((p) => p.name.toLowerCase().includes(term));
  }, [prioritySearch]);

  const filteredAssignees = React.useMemo(() => {
    const list = availableMembers || [];
    if (!assigneeSearch.trim()) return list;
    const term = assigneeSearch.toLowerCase().trim();
    return list.filter(
      (m) =>
        (m.full_name || '').toLowerCase().includes(term) ||
        (m.username || '').toLowerCase().includes(term) ||
        (m.email || '').toLowerCase().includes(term)
    );
  }, [availableMembers, assigneeSearch]);

  const filteredProjects = React.useMemo(() => {
    const list = projects || [];
    if (!projectSearch.trim()) return list;
    const term = projectSearch.toLowerCase().trim();
    return list.filter((p) => (p.name || '').toLowerCase().includes(term));
  }, [projects, projectSearch]);

  const filteredOverdue = React.useMemo(() => {
    if (!overdueSearch.trim()) return OVERDUE_OPTIONS;
    const term = overdueSearch.toLowerCase().trim();
    return OVERDUE_OPTIONS.filter((o) => o.name.toLowerCase().includes(term));
  }, [overdueSearch]);

  const filteredGithub = React.useMemo(() => {
    if (!githubSearch.trim()) return GITHUB_OPTIONS;
    const term = githubSearch.toLowerCase().trim();
    return GITHUB_OPTIONS.filter((o) => o.name.toLowerCase().includes(term));
  }, [githubSearch]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenFilterDropdown(null);
        setStatusSearch('');
        setPrioritySearch('');
        setAssigneeSearch('');
        setProjectSearch('');
        setOverdueSearch('');
        setGithubSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  const resetDropdownState = () => {
    setOpenFilterDropdown(null);
    setStatusSearch('');
    setPrioritySearch('');
    setAssigneeSearch('');
    setProjectSearch('');
    setOverdueSearch('');
    setGithubSearch('');
  };

  const renderFilterDropdown = (
    type,
    label,
    currentValue,
    displayValue,
    onSelect,
    searchValue,
    onSearchChange,
    filteredItems,
    allLabel
  ) => (
    <div className="w-full relative filter-dropdown">
      <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpenFilterDropdown(openFilterDropdown === type ? null : type)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">{displayValue || allLabel || `All ${label}`}</span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${
            openFilterDropdown === type ? 'rotate-180' : ''
          }`}
        />
      </button>
      {openFilterDropdown === type && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
          <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                className="input-field w-full !pl-9"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            <button
              type="button"
              onClick={() => {
                onSelect('');
                setOpenFilterDropdown(null);
                onSearchChange('');
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                !currentValue
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                  : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
              }`}
            >
              {allLabel || `All ${label}`}
            </button>
            {filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onSelect(item.id);
                  setOpenFilterDropdown(null);
                  onSearchChange('');
                }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  String(currentValue) === String(item.id)
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                }`}
              >
                {item.name || item.full_name || item.username}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">
                No matches
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const selectedAssignee = (availableMembers || []).find(
    (m) => String(m.id) === String(filters.assignee)
  );
  const selectedProject = (projects || []).find(
    (p) => String(p.id) === String(filters.project_id)
  );
  const selectedPriority = PRIORITY_OPTIONS.find((o) => o.id === filters.priority);
  const selectedOverdue = OVERDUE_OPTIONS.find((o) => o.id === filters.is_overdue);
  const selectedGithub = GITHUB_OPTIONS.find((o) => o.id === filters.github_integration);
  const firstAssignee = (task) => {
    const id = Array.isArray(task.assigned_to) && task.assigned_to[0];
    if (!id) return null;
    return membersById.get(id) || { id, full_name: 'Assignee' };
  };

  const estimatedLabel = (task) => {
    if (task.estimated_hours == null || task.estimated_hours === '') return '—';
    const hours = Number(task.estimated_hours);
    if (Number.isNaN(hours)) return '—';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const spentLabel = (task) => {
    const seconds = task.total_time_info?.total_time;
    if (seconds == null) return '—';
    return formatDurationHM(seconds);
  };

  const statusPillColor = (statusName) => {
    const known = ['To Do', 'In Progress', 'Done', 'Completed'];
    if (known.includes(statusName)) return undefined;
    return getStatusColor?.(statusName);
  };

  const hasActiveFilters =
    Object.values(filters || {}).some((v) => v !== '') || Boolean(searchTerm);

  // Prefer /tasks/statistics (full DB counts). Fall back to the loaded list when unavailable.
  const activeCount =
    statistics?.active_tasks ??
    filteredTasks.filter((t) => ACTIVE_STATUSES.has(t.status || 'To Do')).length;
  const completedCount =
    statistics?.completed_tasks ??
    filteredTasks.filter((t) => COMPLETED_STATUSES.has(t.status)).length;
  const totalTasksCount = statistics?.total_tasks ?? serverTotal ?? filteredTasks.length;
  // Matched rows from server (filtered list total), not just the current page
  const matchedTotal = serverTotal ?? filteredTasks.length;

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">{title}</h1>
          {subtitle && (
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">{subtitle}</p>
          )}
          {filterBanner}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === 'project_lead' && typeof onTaskScopeChange === 'function' && (
            <div
              className="inline-flex items-center rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 p-0.5"
              role="group"
              aria-label="Task scope"
            >
              <button
                type="button"
                onClick={() => onTaskScopeChange('all')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  taskScope === 'all'
                    ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                All tasks
              </button>
              <button
                type="button"
                onClick={() => onTaskScopeChange('mine')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  taskScope === 'mine'
                    ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                }`}
              >
                <User className="h-3.5 w-3.5" />
                My tasks
              </button>
            </div>
          )}
          {(user?.role === 'admin' || user?.role === 'project_lead') && (
            <button type="button" onClick={onCreate} className="btn-primary flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Task
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={CheckSquare}
          tone="primary"
          label="Total Tasks"
          value={totalTasksCount}
        />
        <StatCard icon={Play} tone="green" label="Active Tasks" value={activeCount} />
        <StatCard
          icon={CheckCircle2}
          tone="green"
          label="Completed Tasks"
          value={completedCount}
        />
      </div>

      {/* Search + Filters — same pattern as Attendance / Projects */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search tasks by name, description, or tags..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="text-secondary-400 hover:text-secondary-600"
              title="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onToggleFilters}
            className="btn-secondary flex items-center gap-2"
          >
            <FilterIcon className="h-4 w-4" />
            Filters
            <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              {renderFilterDropdown(
                'status',
                'Status',
                filters.status,
                filters.status || '',
                (value) => onFilterChange('status', value),
                statusSearch,
                setStatusSearch,
                filteredStatuses,
                'All Statuses'
              )}
              {renderFilterDropdown(
                'priority',
                'Priority',
                filters.priority,
                selectedPriority?.name || '',
                (value) => onFilterChange('priority', value),
                prioritySearch,
                setPrioritySearch,
                filteredPriorities,
                'All Priorities'
              )}
              {renderFilterDropdown(
                'assignee',
                'Assignee',
                filters.assignee,
                selectedAssignee
                  ? selectedAssignee.full_name || selectedAssignee.username || ''
                  : '',
                (value) => onFilterChange('assignee', value),
                assigneeSearch,
                setAssigneeSearch,
                filteredAssignees,
                'All Members'
              )}
              {renderFilterDropdown(
                'project',
                'Project',
                filters.project_id,
                selectedProject?.name || '',
                (value) => onFilterChange('project_id', value),
                projectSearch,
                setProjectSearch,
                filteredProjects,
                'All Projects'
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 items-end">
              {renderFilterDropdown(
                'overdue',
                'Overdue Status',
                filters.is_overdue,
                selectedOverdue?.name || '',
                (value) => onFilterChange('is_overdue', value),
                overdueSearch,
                setOverdueSearch,
                filteredOverdue,
                'All Tasks'
              )}
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Due Date After
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.due_date_after}
                  onChange={(e) => onFilterChange('due_date_after', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Due Date Before
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.due_date_before}
                  onChange={(e) => onFilterChange('due_date_before', e.target.value)}
                />
              </div>
              {renderFilterDropdown(
                'github',
                'GitHub Integration',
                filters.github_integration,
                selectedGithub?.name || '',
                (value) => onFilterChange('github_integration', value),
                githubSearch,
                setGithubSearch,
                filteredGithub,
                'All Tasks'
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                {matchedTotal.toLocaleString()} task{matchedTotal !== 1 ? 's' : ''} match
                {hasActiveFilters && (
                  <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 rounded-full text-xs">
                    Filtered
                  </span>
                )}
                {isFetching && (
                  <span className="ml-2 text-xs text-secondary-400">Updating…</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && (
                  <button
                    type="button"
                    onClick={onToggleDeleted}
                    className="btn-secondary text-sm flex items-center gap-1.5"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    {showDeletedTasks
                      ? 'Active Tasks'
                      : `Deleted (${deletedTasksCount || deletedTasks.length || 0})`}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    resetDropdownState();
                    onClearFilters();
                  }}
                  className="btn-secondary text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {user?.role === 'admin' && showDeletedTasks ? (
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-rose-600 flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Deleted Tasks ({deletedTasksCount || deletedTasks.length})
            </h3>
            <button type="button" onClick={onToggleDeleted} className="btn-secondary text-sm flex items-center gap-1">
              <XIcon className="h-3.5 w-3.5" />
              Back to tasks
            </button>
          </div>
          {deletedTasksLoading ? (
            <div className="py-16 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : deletedTasks.length === 0 ? (
            <div className="py-16 text-center text-secondary-500">No deleted tasks</div>
          ) : (
            <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {deletedTasks.map((task) => (
                <div key={task.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">{task.name}</p>
                    <p className="text-xs text-secondary-500 truncate">{task.project_name || 'Unknown project'}</p>
                  </div>
                  <button type="button" onClick={() => onRestore(task.id)} className="btn-primary text-sm">
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : matchedTotal === 0 ? (
        <div className="card text-center py-16">
          <CheckSquare className="h-14 w-14 text-secondary-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">
            {searchTerm || hasActiveFilters
              ? 'No tasks found'
              : user?.role === 'member'
                ? 'No assigned tasks'
                : 'No tasks yet'}
          </h3>
          <p className="text-sm text-secondary-500 mb-4">
            {searchTerm
              ? `No tasks match "${searchTerm}"`
              : hasActiveFilters
                ? 'No tasks match the current filters'
                : user?.role === 'member'
                  ? "You haven't been assigned to any tasks yet"
                  : 'Create your first task to get started'}
          </p>
          {(user?.role === 'admin' || user?.role === 'project_lead') && !searchTerm && (
            <button type="button" onClick={onCreate} className="btn-primary">
              Create Task
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-secondary-200 dark:divide-secondary-700">
            {paginatedTasks.map((task) => {
              const assignee = firstAssignee(task);
              const canManage = canManageTask(task);
              const statusName = task.status || 'To Do';
              const projectName = getProjectName(task.project_id);
              return (
                <div
                  key={task.id}
                  className="p-4 space-y-2 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-800/60"
                  onClick={() => onView(task.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {task.name}
                        </p>
                        {task.parent_task_id && (
                          <ClipboardList className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`h-2 w-2 rounded-full shrink-0 ${projectDotColor(task.project_id)}`} />
                        <span className="text-xs text-secondary-500 truncate">{projectName}</span>
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={[
                          { label: 'Open', icon: Eye, onClick: () => onView(task.id) },
                          { label: 'Open in new tab', icon: ExternalLink, onClick: () => onOpenInNewWindow(task.id) },
                          { label: 'Edit', icon: Edit, onClick: () => onEdit(task), hidden: !canManage },
                          { label: 'Delete', icon: Trash2, onClick: () => onDelete(task.id), danger: true, hidden: !canManage },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill
                      status={statusName === 'In Progress' ? 'In Progress Soft' : statusName}
                      colorClass={statusName === 'In Progress' ? undefined : statusPillColor(statusName)}
                      showDot={false}
                    />
                    <span className="text-xs text-secondary-500">
                      {assignee ? (assignee.full_name || assignee.username) : 'Unassigned'}
                    </span>
                    <span className="text-xs text-secondary-400 tabular-nums ml-auto">
                      {spentLabel(task)} / {estimatedLabel(task)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
              <thead className="bg-secondary-50 dark:bg-secondary-800/50">
                <tr>
                  {['Status', 'Task', 'Project', 'Assignee', 'Time Est.', 'Time Spent', ''].map((col) => (
                    <th
                      key={col || 'actions'}
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700 bg-white dark:bg-secondary-800">
                {paginatedTasks.map((task) => {
                  const assignee = firstAssignee(task);
                  const canManage = canManageTask(task);
                  const statusName = task.status || 'To Do';
                  const projectName = getProjectName(task.project_id);

                  return (
                    <tr
                      key={task.id}
                      onClick={() => onView(task.id)}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-800/60 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <StatusPill
                          status={statusName === 'In Progress' ? 'In Progress Soft' : statusName}
                          colorClass={statusName === 'In Progress' ? undefined : statusPillColor(statusName)}
                          showDot={false}
                        />
                      </td>
                      <td className="px-4 py-3.5 min-w-[200px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                            {task.name}
                          </p>
                          {task.parent_task_id && (
                            <span title="Subtask">
                              <ClipboardList className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`h-2 w-2 rounded-full flex-shrink-0 ${projectDotColor(task.project_id)}`}
                          />
                          <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate max-w-[140px]">
                            {projectName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {assignee ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar
                              blobName={assignee.avatar_blob_name || assignee.avatar || null}
                              alt={assignee.full_name || assignee.username || 'User'}
                              size="xs"
                              fallbackText={(assignee.full_name || assignee.username || '?')
                                .split(/\s+/)
                                .map((p) => p[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            />
                            <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate max-w-[120px]">
                              {assignee.full_name || assignee.username}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-secondary-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-secondary-600 dark:text-secondary-400 whitespace-nowrap tabular-nums">
                        {estimatedLabel(task)}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-secondary-600 dark:text-secondary-400 whitespace-nowrap tabular-nums">
                        {spentLabel(task)}
                      </td>
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu
                          items={[
                            { label: 'Open', icon: Eye, onClick: () => onView(task.id) },
                            {
                              label: 'Open in new tab',
                              icon: ExternalLink,
                              onClick: () => onOpenInNewWindow(task.id),
                            },
                            {
                              label: 'Edit',
                              icon: Edit,
                              onClick: () => onEdit(task),
                              hidden: !canManage,
                            },
                            {
                              label: 'Delete',
                              icon: Trash2,
                              onClick: () => onDelete(task.id),
                              danger: true,
                              hidden: !canManage,
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 border-t border-secondary-200 dark:border-secondary-700">
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              totalItems={matchedTotal}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              itemLabel="tasks"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksListView;
